import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService, type K8sResourceListResponse } from '@/services/k8sResourceService'
import SearchableSelector from '@/components/ui/SearchableSelector'

interface StorageConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function StorageConfigEditor({ flow, canEdit, onSave }: StorageConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [editedEnabled, setEditedEnabled] = useState(false)
  const [editedSize, setEditedSize] = useState('')
  const [editedClassName, setEditedClassName] = useState('')
  const [editedExistingPVC, setEditedExistingPVC] = useState('')
  const [editedDeletionPolicy, setEditedDeletionPolicy] = useState('')

  // PVCs state
  const [pvcs, setPvcs] = useState<K8sResourceListResponse | null>(null)
  const [pvcNames, setPvcNames] = useState<string[]>([])
  const [pvcsLoading, setPvcsLoading] = useState(false)

  // Storage Classes state
  const [storageClasses, setStorageClasses] = useState<string[]>([])
  const [storageClassesLoading, setStorageClassesLoading] = useState(false)

  // Fetch PVCs when editing
  useEffect(() => {
    const fetchPVCs = async () => {
      if (!isEditing || !flow.metadata?.namespace) return

      setPvcsLoading(true)
      try {
        const pvcData = await k8sResourceService.getPVCs(flow.metadata.namespace)
        setPvcs(pvcData)
        const names = k8sResourceService.extractPVCNames(pvcData)
        setPvcNames(names)
      } catch (error) {
        console.error('Failed to fetch PVCs:', error)
        setPvcs(null)
        setPvcNames([])
      } finally {
        setPvcsLoading(false)
      }
    }

    fetchPVCs()
  }, [isEditing, flow.metadata?.namespace])

  // Fetch Storage Classes when editing
  useEffect(() => {
    const fetchStorageClasses = async () => {
      if (!isEditing) return

      setStorageClassesLoading(true)
      try {
        const classes = await k8sResourceService.getStorageClasses()
        const classNames = k8sResourceService.extractStorageClassNames(classes)
        setStorageClasses(classNames)
      } catch (error) {
        console.error('Failed to fetch storage classes:', error)
        setStorageClasses([])
      } finally {
        setStorageClassesLoading(false)
      }
    }

    fetchStorageClasses()
  }, [isEditing])

  // Get PVC size from the selected PVC
  const getSelectedPVCSize = (): string => {
    if (!editedExistingPVC || !pvcs) return ''
    const size = k8sResourceService.getPVCSize(pvcs, editedExistingPVC)
    return size || 'Unknown'
  }

  const handleEdit = () => {
    setEditedEnabled(flow.spec?.storage?.enabled || false)
    setEditedSize(flow.spec?.storage?.size || '1Gi')
    setEditedClassName(flow.spec?.storage?.className || '')
    setEditedExistingPVC(flow.spec?.storage?.existingPVCName || '')
    setEditedDeletionPolicy(flow.spec?.storage?.deletionPolicy || 'Retain')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        storage: editedEnabled ? {
          enabled: true,
          size: editedExistingPVC.trim() ? undefined : (editedSize.trim() || '1Gi'),
          className: editedClassName.trim() || undefined,
          existingPVCName: editedExistingPVC.trim() || undefined,
          deletionPolicy: editedDeletionPolicy.trim() || 'Retain'
        } : {
          enabled: false
        }
      }
    }

    // Clean up undefined fields
    if (updatedFlow.spec.storage.enabled) {
      if (!updatedFlow.spec.storage.size) delete updatedFlow.spec.storage.size
      if (!updatedFlow.spec.storage.className) delete updatedFlow.spec.storage.className
      if (!updatedFlow.spec.storage.existingPVCName) delete updatedFlow.spec.storage.existingPVCName
    }

    onSave(updatedFlow, t('detail.storageConfig'))
    setIsEditing(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.storageConfig', 'Storage Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Enabled */}
          <div className="flex items-center">
            <input
              id="storageEnabled"
              type="checkbox"
              checked={editedEnabled}
              onChange={(e) => setEditedEnabled(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="storageEnabled" className="ml-2 block text-sm text-gray-900">
              {t('detail.storageEnabled', 'Enabled')}
            </label>
          </div>

          {editedEnabled && (
            <>
              {/* Existing PVC */}
              <div>
                <SearchableSelector
                  title={t('detail.storageExistingPVC', 'Existing PVC')}
                  value={editedExistingPVC}
                  onChange={(val) => setEditedExistingPVC(val)}
                  options={pvcNames}
                  placeholder={t('common:flowConfig.selectPvcOrCreate')}
                  loading={pvcsLoading}
                  disabled={false}
                  emptyMessage="No PVCs available in this namespace"
                  showCount={false}
                />
                <p className="mt-1 text-xs text-gray-500">{t('common:flowConfig.existingPvcHint')}</p>
                {editedExistingPVC && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-blue-900">{t('common:flowConfig.selectedPvcInfo')}</p>
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-blue-700">
                            <span className="font-medium">Name:</span> {editedExistingPVC}
                          </p>
                          <p className="text-xs text-blue-700">
                            <span className="font-medium">Size:</span> {getSelectedPVCSize()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!editedExistingPVC.trim() && (
                <>
                  {/* Size */}
                  <div>
                    <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('detail.storageSize', 'Size')}
                    </label>
                    <input
                      id="size"
                      type="text"
                      value={editedSize}
                      onChange={(e) => setEditedSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                      placeholder="1Gi"
                    />
                  </div>

                  {/* Storage Class */}
                  <div>
                    <SearchableSelector
                      title={t('detail.storageClassName', 'Storage Class')}
                      value={editedClassName}
                      onChange={(val) => setEditedClassName(val)}
                      options={storageClasses}
                      placeholder={t('common:flowConfig.selectStorageClass')}
                      loading={storageClassesLoading}
                      disabled={false}
                      emptyMessage="No storage classes available"
                      showCount={false}
                    />
                  </div>
                </>
              )}

              {/* Deletion Policy */}
              <div>
                <label htmlFor="deletionPolicy" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('detail.storageDeletionPolicy', 'Deletion Policy')}
                </label>
                <select
                  id="deletionPolicy"
                  value={editedDeletionPolicy}
                  onChange={(e) => setEditedDeletionPolicy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="Retain">Retain</option>
                  <option value="Delete">Delete</option>
                </select>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.storageEnabled', 'Enabled')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.storage?.enabled ? 'Yes' : 'No'}</dd>
          </div>
          {flow.spec?.storage?.enabled && (
            <>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.storageSize', 'Size')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.storage.size || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.storageClassName', 'Storage Class')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.storage.className || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.storageExistingPVC', 'Existing PVC')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.storage.existingPVCName || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.storageDeletionPolicy', 'Deletion Policy')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.storage.deletionPolicy || '-'}</dd>
              </div>
            </>
          )}
        </dl>
      )}
    </div>
  )
}
